import React, { useEffect, useState } from 'react';

import Modal from 'components/modal';
import AddByEmails from './AddByEmails';
import ToggleButtons from 'components/toggleButtons/toggleButtons';
import { BodyContainer, HeaderContainer, InnerContainer, Title } from './styles';
import { MODAL_SIZES } from 'components/modal/types';
import 'react-multi-email/style.css';
import { isMobile } from 'react-device-detect';
import AddByLink from './AddByLink/AddByLink';
import { Invite } from './AddByEmails/invitesReducer';
import { AccountAddons, AccountAvailableAddon, Domains } from 'contexts/accounts/types';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import { INVITE_TABS_COPY, LEGACY_INVITE_TABS_COPY } from 'constants/invites';

interface IInviteUsers {
  isEditingInvite?: boolean;
  onClose: (result?: 'ok' | 'error', invites?: Invite[]) => void;
  inviteAccOwner?: boolean;
  account: AccountInvite;
}

export interface AccountInvite {
  id: string;
  licenses_available?: number | boolean;
  email_domains?: Domains[];
  name?: string;
  account_available_addons?: AccountAvailableAddon[];
  // Only the full account response carries this; the `/me` summary doesn't.
  account_addons?: Pick<AccountAddons, 'plan_addons'>;
}

// With teams, the design's access step is wider, to fit the Skill Dive column next to them.
export const SKILL_DIVE_ACCESS_STEP_WIDTH = 960;

// The design's narrow steps (emails, message, link) are slightly narrower than MODAL_SIZES.SMALL.
export const INVITE_NARROW_STEP_WIDTH = 595;

export enum InviteType {
  BY_EMAIL = 'by_email',
  BY_LINK = 'by_link',
}

export const INVITE_TYPE_OPTIONS = {
  firstOption: { value: INVITE_TABS_COPY.BY_EMAIL, id: InviteType.BY_EMAIL },
  secondOption: { value: INVITE_TABS_COPY.BY_LINK, id: InviteType.BY_LINK },
};

const LEGACY_INVITE_TYPE_OPTIONS = {
  firstOption: { value: LEGACY_INVITE_TABS_COPY.BY_EMAIL, id: InviteType.BY_EMAIL },
  secondOption: { value: LEGACY_INVITE_TABS_COPY.BY_LINK, id: InviteType.BY_LINK },
};

const InviteUsers: React.FC<IInviteUsers> = ({
  isEditingInvite,
  onClose,
  inviteAccOwner,
  account,
}) => {
  const [step, setStep] = useState(0);
  const [hasTeams, setHasTeams] = useState(false);
  const [inviteType, setInviteType] = useState<InviteType>(InviteType.BY_EMAIL);
  const title = inviteAccOwner
    ? `Invite users to join ${account?.name} account`
    : 'Invite users to join INE Business';

  // Account owner invites don't take Skill Dive, so they keep today's widths.
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW) && !inviteAccOwner;

  // The link tab keeps its own size; `step` is the email tab's and outlives a switch of tab.
  const modalSize =
    (inviteType === InviteType.BY_LINK && INVITE_NARROW_STEP_WIDTH) ||
    (step === 0 && INVITE_NARROW_STEP_WIDTH) ||
    (step === 1 && MODAL_SIZES.LARGE) ||
    (step === 2 &&
      (skillDiveFlow && hasTeams ? SKILL_DIVE_ACCESS_STEP_WIDTH : MODAL_SIZES.LARGE)) ||
    (step === 3 && INVITE_NARROW_STEP_WIDTH);

  useEffect(() => {
    setStep(0);
  }, []);

  const handleInvitesSubmmit = (ok: boolean, invites?: Invite[]) => {
    onClose(ok ? 'ok' : 'error', invites);
  };

  return (
    <Modal
      centered
      onCancel={() => onClose()}
      visible={isEditingInvite}
      fullscreen={isMobile}
      footer={null}
      closeIconColor="#FFF"
      width={isMobile ? '100%' : modalSize}
    >
      <InnerContainer>
        <HeaderContainer>
          <Title>{title} </Title>
          {!inviteAccOwner && (
            <ToggleButtons
              {...(skillDiveFlow ? INVITE_TYPE_OPTIONS : LEGACY_INVITE_TYPE_OPTIONS)}
              onChange={(option) => setInviteType(option as InviteType)}
            />
          )}
        </HeaderContainer>
        <BodyContainer>
          {inviteType === InviteType.BY_EMAIL && (
            <AddByEmails
              onStepChange={(step) => setStep(step)}
              onTeamsChange={setHasTeams}
              onInvitesSubmit={handleInvitesSubmmit}
              inviteAccOwner={inviteAccOwner}
              account={account}
            />
          )}
          {inviteType === InviteType.BY_LINK && <AddByLink handleBackPress={() => onClose()} />}
        </BodyContainer>
      </InnerContainer>
    </Modal>
  );
};

export default InviteUsers;
